import React, { FC, PropsWithChildren, startTransition, useCallback, useId } from 'react';
import styled from 'styled-components';

import { Button, Icon, Tooltip } from '@column/column-ui-kit';

import { useViewTransition } from '~/hooks/useViewTransition';

interface ClearProps extends PropsWithChildren {
  isActive: boolean;
  onClear?: () => void;
  tooltipContent?: string;
}

const Wrapper = styled.div`
  position: relative;
`;

const Field = styled.div<{ $isActive: boolean }>`
  width: ${({ $isActive }) => ($isActive ? 'calc(100% - 44px)' : '100%')};
  transition: width 0.3s ease;
  isolation: isolate;
`;

const ClearButton = styled(Tooltip)<{ $isVisible: boolean; $id: string }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transform: translateX(${({ $isVisible }) => ($isVisible ? '0' : '4px')});
  view-transition-name: clear-button-${({ $id }) => $id};
  position: absolute;
  top: 2px;
  right: 2px;

  svg {
    --icon-size: 20px;
  }
`;

export const Clear: FC<ClearProps> = ({ children, isActive, onClear, tooltipContent }) => {
  const isVisible = useViewTransition(isActive);
  const id = useId();

  const handleClick = useCallback(() => {
    startTransition(() => {
      onClear?.();
    });
  }, [onClear]);

  return (
    <Wrapper>
      <Field $isActive={isVisible}>{children}</Field>

      <ClearButton
        $id={id}
        content={tooltipContent ?? 'Clear filters'}
        placement="top"
        zIndex={15}
        $isVisible={isVisible}
      >
        <Button variant="subtle" size="small" onClick={handleClick} icon={<Icon.Reset />} type="button" />
      </ClearButton>
    </Wrapper>
  );
};
